import { makeValuesPredicate } from '@sgme/fp';

import type { UserType } from '@/api/user/UserType.ts';
import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import type { FxCash } from '@/store/cash/state.ts';
import type { FxFlexiForward } from '@/store/flexi-forward/state.ts';
import type { FxForwardAccumulator } from '@/store/forwardAccumulator/state.ts';
import type { FxOption } from '@/store/option/state.ts';
import { limitOrdersTypes, type Order, type OrderType } from '@/store/orders/state.ts';
import type { FxSwap } from '@/store/swap/state.ts';
import type { FxTargetAccumulator } from '@/store/targetAccumulator/state.ts';

export type GetCurrentUserResponse = {
  /**
   * E-mail
   */
  login: string;
  ulysseLogin: string;
  bdrInitials: string;
  userType: UserType;
  isOnBehalf: boolean;
  isFastTrade: boolean;
  market: null;
  canTrade: boolean;
  piwikLogin: string;
  hasMifid2: boolean;
  hasFxTcaAccess: boolean;
  clients: Client[];
  featureToggles: {
    myFxPredeliver: boolean;
    myFxRollover: boolean;
    myFxTargetAccumulator: boolean;
    myFxSquareTargetAccumulator: boolean;
    myFxForwardAccumulator: boolean;
    myFxVanillaAccumulator: boolean;
    myFxPauseTargetAccumulator: boolean;
    ndfEspExecutable: boolean;
    barrierTypeDropdownDisplayPIVOTEKI: boolean;
    barrierTypeDropdownDisplayPIVOT: boolean;
    barrierTypeDropdownDisplayEKI: boolean;
    accumulatorAllowOverrideHedge: boolean;
    optionBinary: boolean;
    optionTrigger: boolean;
    premiumGappedMid: boolean;
    smartRFS: boolean;
    flexiForward: boolean;
  };
  productsAccess: ProductsAccess;
  localMarketsCcy: string[];
  userPreferences: {
    emails: string[];
    showSwapPoints: boolean;
    defaultNotional: string;
    orderQuickLimit: boolean;
    showDeltaStrike: boolean;
    showMyMainDeals: boolean;
    showVoiceTimestampingPanel: boolean;
    defaultHedgeType: string; // "Live",
    defaultPriceType: string; // "AMOUNT",
    accountNameLength: 'Short' | 'Legal'; // "Long",
    oneClickExecution: boolean;
    splitNotifications: boolean;
    autoCloseGrowlAfter: null;
    dateInputCultureInfo: string; // "fr",
    defaultCashExpiryDate: string; // "2025-01-01",
    extendedSwapPrecision: boolean;
    optionAlignCurrencies: boolean;
    optionIsPremiumNetted: boolean;
    optionOneWayQuotation: boolean;
    defaultCashMaturityDate: '';
    optionExpandGreekAndMkt: boolean;
    optionStrategyGroupLegs: boolean;
    optionDisplayMidVolatility: boolean;
    splitNotificationsEmailsCash: string[];
    resetTileOnSuccessfulExecution: boolean;
    splitNotificationsEmailsOrders: string[];
    resetTileToSpotOnQuoteExecution: boolean;
    splitNotificationsEmailsOptions: string[];
  };
  productTypes: ProductType[];
};

export interface Client {
  companyId: number;
  mnemonicCode: string | null;
  companyName: string;
  companyFullName?: string | null;
  externalCompanyName: string | null;
  bookingEntityName: string | null;
  primeBroker: string | null;
  maxTenor: string | null;
}

export type ProductsAccess = {
  spot: boolean;
  forward: boolean;
  swap: boolean;
  option: boolean;
  forwardAccu: boolean;
  targetAccu: boolean;
  nonDeliverable: boolean;
  stopLoss: boolean;
  takeProfit: boolean;
  callOrder: boolean;
  fixing: boolean;
  twap: boolean;
  nightjar: boolean;
  falcon: boolean;
  americanForward: boolean;
};

export const isLimitOrder = makeValuesPredicate(limitOrdersTypes);
export const isFixingOrder = makeValuesPredicate(['Fixing']);

export type ProductType =
  | 'Cash'
  | 'Swap'
  | 'Option'
  | 'FlexiForward'
  | 'TargetAccumulator'
  | 'ForwardAccumulator'
  | 'SmartRfs'
  | OrderType;

export type Products = Order | FxCash | FxSwap | FxOption | FxFlexiForward | FxTargetAccumulator | FxForwardAccumulator;

export const getCurrentUser = (builder: WebapiEndpointBuilder) => {
  return builder.query<GetCurrentUserResponse, void>({
    query: () => ({
      url: '/user/current',
      method: 'GET',
    }),
    providesTags: ['getCurrentUser'],
  });
};
